import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadPilotReportHistoryCsv,
  downloadPilotReportHistoryPdf,
  downloadPilotReportCsv,
  downloadPilotReportPdf,
  fetchPilotReportPreviewData,
  fetchPilotReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

const AIRCRAFT_TYPE_OPTIONS = [
  { value: '', label: 'All types (Helo + Fixed Wing)' },
  { value: 'Helicopter', label: 'Helicopter' },
  { value: 'Fixed Wing', label: 'Fixed Wing' }
];

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function addDays(dateValue, days) {
  const d = new Date(`${dateValue}T00:00:00`);
  d.setDate(d.getDate() + days);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function complianceColor(status) {
  if (!status) return 'text.secondary';
  if (status.startsWith('NON-COMPLIANT')) return 'error.main';
  if (status.startsWith('PART III')) return 'warning.main';
  return 'success.main';
}

const PilotReportPanel = ({ aircraftOptions = [], aircraftLabelById = {} }) => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [crewId, setCrewId] = useState('');
  const [crewOptions, setCrewOptions] = useState([]);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => addDays(todayDateValue(), -90));
  const [dateTo, setDateTo] = useState(() => todayDateValue());
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [aircraftType, setAircraftType] = useState('');
  const [nonCompliantOnly, setNonCompliantOnly] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/report-center/filter-options/crew')
      .then((res) => setCrewOptions(res.data.options || []))
      .catch(() => setCrewOptions([]));
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchPilotReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const missingPilot = !crewId;

  const filtersPayload = useMemo(() => ({
    crewId: crewId || undefined,
    aircraftType: aircraftType || undefined,
    nonCompliantOnly: nonCompliantOnly || undefined
  }), [crewId, aircraftType, nonCompliantOnly]);

  const requestBody = useMemo(() => ({
    crewId,
    aircraftIds: selectedAircraftIds,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate,
    filters: filtersPayload
  }), [crewId, selectedAircraftIds, dateFrom, dateTo, reportDate, filtersPayload]);

  const selectedCrewLabel = useMemo(() => {
    const opt = crewOptions.find((o) => o.value === crewId);
    return opt?.label || 'Pilot';
  }, [crewOptions, crewId]);

  const filenameSuffix = useMemo(() => {
    const safeName = String(selectedCrewLabel).replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 40);
    const period = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : 'all';
    return `${safeName}_${period}`;
  }, [selectedCrewLabel, dateFrom, dateTo]);

  const handlePreview = async () => {
    if (missingPilot) {
      showToast('Select a pilot', 'warning');
      return;
    }
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchPilotReportPreviewData(requestBody);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (missingPilot) {
      showToast('Select a pilot', 'warning');
      return;
    }
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchPilotReportPreviewData(requestBody);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (missingPilot) {
      showToast('Select a pilot', 'warning');
      return;
    }
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadPilotReportPdf(
        { ...requestBody, visualizations },
        `Pilot_Report_${filenameSuffix}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'Pilot Report with visualizations downloaded and saved to history'
          : 'Pilot Report downloaded and saved to history',
        'success'
      );
      if (subTab === 1) loadHistory();
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (missingPilot) {
      showToast('Select a pilot', 'warning');
      return;
    }
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadPilotReportCsv(requestBody, `Pilot_Report_${filenameSuffix}.pdf`);
      showToast('Pilot Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryPdf = (row) => {
    downloadPilotReportHistoryPdf(row._id, row.filename || 'Pilot_Report.pdf');
  };

  const handleHistoryCsv = (row) => {
    downloadPilotReportHistoryCsv(row._id, row.filename || 'Pilot_Report.pdf');
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Pilot Report
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Complete pilot file with licences, training, duty history, and a live DGCA FDTL check
        against the operator profile (CAR Section 7 Series J). Select a pilot and date range.
      </Typography>

      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        <Tab label="Generate" />
        <Tab label="History" icon={<HistoryIcon fontSize="small" />} iconPosition="start" />
      </Tabs>

      {subTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="pilot-report-crew-label">Pilot</InputLabel>
                <Select
                  labelId="pilot-report-crew-label"
                  value={crewId}
                  label="Pilot"
                  onChange={(e) => setCrewId(e.target.value)}
                >
                  {crewOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="pilot-report-aircraft-label">Aircraft (optional, sectors)</InputLabel>
                <Select
                  labelId="pilot-report-aircraft-label"
                  multiple
                  value={selectedAircraftIds}
                  label="Aircraft (optional, sectors)"
                  onChange={(e) => setSelectedAircraftIds(e.target.value)}
                  renderValue={(selected) => selected.map((id) => aircraftLabelById[id] || id).join(', ')}
                >
                  {aircraftOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Duty from"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Duty to"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Report date"
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={reportDate}
                onChange={(e) => setReportDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Aircraft type</InputLabel>
                <Select
                  value={aircraftType}
                  label="Aircraft type"
                  onChange={(e) => setAircraftType(e.target.value)}
                >
                  {AIRCRAFT_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={nonCompliantOnly}
                    onChange={(e) => setNonCompliantOnly(e.target.checked)}
                  />
                }
                label="Non-compliant duties only"
              />
            </Grid>
          </Grid>

          {missingPilot && (
            <Alert severity="info" sx={{ mt: 2 }}>Select a pilot to generate the report.</Alert>
          )}
          {dateRangeError && (
            <Alert severity="warning" sx={{ mt: 2 }}>{dateRangeError}</Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={previewing ? <CircularProgress size={18} /> : <Preview />}
              onClick={handlePreview}
              disabled={previewing || Boolean(dateRangeError) || missingPilot}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              startIcon={visualizing ? <CircularProgress size={18} /> : <BarChartIcon />}
              onClick={handleVisualize}
              disabled={visualizing || Boolean(dateRangeError) || missingPilot}
            >
              Visualize
            </Button>
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <Download />}
              onClick={() => handleDownload()}
              disabled={downloading || Boolean(dateRangeError) || missingPilot}
            >
              Download PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={downloadingCsv ? <CircularProgress size={18} /> : <Download />}
              onClick={handleDownloadCsv}
              disabled={downloadingCsv || Boolean(dateRangeError) || missingPilot}
            >
              Download CSV
            </Button>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Downloaded Pilot Reports</Typography>
            <Button startIcon={<Refresh />} onClick={loadHistory} disabled={historyLoading}>
              Refresh
            </Button>
          </Stack>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">No Pilot Report downloads yet. Generate one from the Generate tab.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Pilot</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>DGCA status</TableCell>
                  <TableCell>Duties</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id}>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>{row.crewLabel || '—'}</TableCell>
                    <TableCell>
                      {formatDisplayDate(row.dateFrom)} – {formatDisplayDate(row.dateTo)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: complianceColor(row.complianceStatus) }}>
                        {row.complianceStatus || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.rowCount ?? '—'}</TableCell>
                    <TableCell>{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleHistoryPdf(row)}>PDF</Button>
                        <Button size="small" onClick={() => handleHistoryCsv(row)}>CSV</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="Pilot Report preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="Pilot Report"
        data={visualizeData}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdf={handleDownload}
      />
    </Box>
  );
};

export default PilotReportPanel;
